/**
 * useNotifications — React Query-backed notification list + realtime.
 *
 * One cache entry per user holds the full list; `unreadCount` is derived
 * via `select` so mutations only need to invalidate one key. A realtime
 * channel + visibility/app-state listeners invalidate on remote changes.
 */
import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { listNotificationsForUser, getFollowRelation } from "@/data/raw";
import { keys } from "@/data/keys";
import { useAuth } from "@/hooks/useAuth";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from "@/data/mutations/useNotificationMutations";

export type NotificationType =
  | "follower"
  | "badge"
  | "burger_suggestion"
  | "place_suggestion"
  | "proposal_result"
  | "burger_checkin"
  | "checkin_like"
  | "expert_review_pending";

export interface NotificationData {
  // Follower notification
  follower_id?: string;
  follower_name?: string | null;
  follower_username?: string | null;
  follower_avatar_url?: string | null;
  followed_at?: string;

  // Proposal notification
  proposal_id?: string;
  entity_type?: string;
  entity_id?: string;
  entity_slug?: string;
  entity_name?: string;
  proposer_id?: string;
  proposer_name?: string | null;
  proposer_avatar_url?: string | null;
  proposed_description?: string;
  field_type?: "description" | "website_url" | "instagram_url" | "image";

  // Proposal result notification
  status?: "accepted" | "rejected";
  was_edited?: boolean;

  // Badge notification
  badge_id?: string;
  badge_name?: string;
  badge_icon?: string | null;
  badge_description?: string | null;
  awarded_at?: string;

  // Burger checkin notification
  checkin_id?: string;
  burger_id?: string;
  burger_name?: string;
  checker_id?: string;
  checker_name?: string | null;
  checker_avatar_url?: string | null;
  rating?: number;
  photo_url?: string | null;
  place_name?: string | null;
  checked_at?: string;

  // Checkin like notification
  liker_id?: string;
  liker_name?: string | null;
  liker_username?: string | null;
  liker_avatar_url?: string | null;
  liked_at?: string;
}

export interface NotificationItem {
  id: string;
  type: NotificationType;
  data: NotificationData;
  created_at: string;
  read_at: string | null;
}

export function useNotifications() {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const qc = useQueryClient();

  const query = useQuery<NotificationItem[]>({
    queryKey: userId
      ? keys.notifications.list(userId)
      : keys.notifications.list("__none__"),
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await listNotificationsForUser(userId!, 50);
      if (error) throw error;
      return ((data ?? []) as Array<{
        id: string;
        type: string;
        data: NotificationData;
        created_at: string;
        read_at: string | null;
      }>).map((item) => ({
        id: item.id,
        type: item.type as NotificationType,
        data: item.data as NotificationData,
        created_at: item.created_at,
        read_at: item.read_at,
      }));
    },
  });

  const notifications = useMemo(() => query.data ?? [], [query.data]);
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read_at).length,
    [notifications],
  );

  // Realtime subscription: invalidate on insert/update for this user.
  useEffect(() => {
    if (!userId) return;
    const invalidate = () =>
      qc.invalidateQueries({ queryKey: keys.notifications.list(userId) });

    const channel = supabase
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        invalidate,
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        invalidate,
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, qc]);

  // Refresh on app foreground / tab visibility so the unread dot reflects
  // server state after the user returns from background.
  useEffect(() => {
    if (!userId) return;

    let lastRun = 0;
    const throttledRefresh = () => {
      const now = Date.now();
      if (now - lastRun < 1000) return;
      lastRun = now;
      qc.invalidateQueries({ queryKey: keys.notifications.list(userId) });
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") throttledRefresh();
    };
    document.addEventListener("visibilitychange", onVisibility);

    let removeAppListener: (() => void) | undefined;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) throttledRefresh();
        });
        removeAppListener = () => handle.remove();
      } catch {
        // Not running in Capacitor; visibility listener is enough.
      }
    })();

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      removeAppListener?.();
    };
  }, [userId, qc]);

  const markAllMutation = useMarkAllNotificationsRead();
  const markOneMutation = useMarkNotificationRead();

  const markAsRead = useCallback(async () => {
    if (!userId) return;
    if (notifications.every((n) => n.read_at)) return;
    await markAllMutation.mutateAsync(userId);
  }, [userId, notifications, markAllMutation]);

  const markSingleAsRead = useCallback(
    async (notificationId: string) => {
      if (!userId) return;
      await markOneMutation.mutateAsync({ id: notificationId, userId });
    },
    [userId, markOneMutation],
  );

  const checkFollowingBack = useCallback(
    async (followerId: string): Promise<boolean> => {
      if (!userId) return false;
      const { data } = await getFollowRelation(userId, followerId);
      return !!data;
    },
    [userId],
  );

  const refresh = useCallback(() => {
    if (!userId) return Promise.resolve();
    return qc.invalidateQueries({ queryKey: keys.notifications.list(userId) });
  }, [userId, qc]);

  return {
    notifications,
    loading: query.isLoading,
    unreadCount,
    markAsRead,
    markSingleAsRead,
    checkFollowingBack,
    refresh,
  };
}
